<template>
  <v-container class="teacher-itin">
    <div v-if="!teacherParam" class="empty-state">
      <h2>Teacher itinerary</h2>
      <p>Open this page with a teacher name in the URL, e.g. <code>?teacher=Sean%20Kelly</code>.</p>
    </div>
    <template v-else>
      <h1>{{ teacherParam }}'s IABF {{ festivalYear }} Schedule</h1>

      <div v-if="loading" class="muted pad-y">Loading…</div>

      <div v-else-if="myClasses.length === 0" class="empty-state">
        <p>No classes found for <strong>{{ teacherParam }}</strong> on the {{ festivalYear }} schedule.</p>
        <p class="muted">Check the spelling in the URL — it must match exactly. Common alternates: full first name, no middle initial.</p>
      </div>

      <template v-else>
        <v-tabs v-model="activeDay" class="day-tabs" background-color="transparent">
          <v-tab v-for="d in daysWithClasses" :key="d.key" :href="`#${d.key}`">{{ d.label }}</v-tab>
        </v-tabs>

        <div v-for="d in daysWithClasses" :key="d.key" v-show="activeDay === d.key">
          <div class="class-list">
            <template v-for="item in dayTimeline(d.key)">
              <div v-if="item.kind === 'meal'" :key="item.id" class="meal-row">
                <span class="meal-time">{{ item.timeLabel || '?' }}</span>
                <span class="meal-title">{{ item.title || 'Meal' }}</span>
                <span v-if="item.room || item.defaultRoom" class="meal-room">📍 {{ item.room || item.defaultRoom }}</span>
              </div>
              <div v-else :key="item.id" class="class-card">
                <div class="card-meta">
                  <span class="meta-time">{{ item.timeLabel || '?' }}</span>
                  <span class="meta-room" v-if="(item.room || item.defaultRoom)">📍 {{ item.room || item.defaultRoom }}</span>
                  <span class="meta-room muted" v-else>📍 Room TBA</span>
                  <span v-if="item.hasAccompanist" class="meta-piano">🎹 Live piano</span>
                </div>
                <div class="card-title">{{ item.title || '(untitled)' }}</div>
                <div v-if="item.notes" class="card-note">{{ item.notes }}</div>
                <p v-if="item.description" class="card-desc">{{ item.description }}</p>
              </div>
            </template>
          </div>
        </div>
      </template>
    </template>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import { iabfForYear } from '@/config/iabf-collections'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'


function parseSlotRange (label) {
  const m = String(label || '').match(/(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?/)
  if (!m) return { start: 0, end: 0 }
  // 12-hour labels: hours 1-6 are PM at IABF (nothing starts before 7 AM).
  const toPm = (h) => (h >= 1 && h <= 6) ? h + 12 : h
  const sh = toPm(parseInt(m[1], 10))
  const sm = m[2] ? parseInt(m[2], 10) : 0
  const eh = toPm(parseInt(m[3], 10))
  const em = m[4] ? parseInt(m[4], 10) : 0
  return { start: sh * 60 + sm, end: eh * 60 + em }
}

function parse24h (s) {
  const m = String(s || '').match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

function classSortKey (c) {
  const startCanon = parse24h(c.startTime || '')
  const endCanon = parse24h(c.endTime || '')
  if (startCanon !== null) {
    return { start: startCanon, end: endCanon !== null ? endCanon : startCanon }
  }
  const fromLabel = parseSlotRange(c.timeLabel || '')
  return { start: fromLabel.start || (c.order || 0), end: fromLabel.end || 0 }
}

const TRACK_LABEL = {
  rehearsal:  'Rehearsal',
  begBallet:  'Beg. Ballet',
  intBallet:  'Int Ballet',
  contemp:    'Contemporary',
  all:        'All Tracks'
}

const DAY_LABEL = {
  tuesday:   'Tuesday',
  wednesday: 'Wednesday',
  thursday:  'Thursday',
  friday:    'Friday'
}

// Character names that should also pull classes onto a teacher's
// itinerary page. Keys + values are lowercased for direct match.
const TEACHER_ALIASES = {
  'mikey cusumano': ['madame olga']
}

const DAY_ORDER = { tuesday: 2, wednesday: 3, thursday: 4, friday: 5 }

export default {
  data () {
    return {
      loading: true,
      classes: [],
      activeDay: 'tuesday'
    }
  },
  computed: {
    festivalYear () {
      return Number(this.$store.getters.adminFestivalYear) ||
        Number(this.$store.getters.iabfDancerYear) ||
        IABF_DANCER_YEAR
    },
    teacherParam () {
      const raw = this.$route.query.teacher
      return raw ? String(raw).trim() : ''
    },
    matchNames () {
      // Names that should pull classes onto this teacher's page. Includes
      // the URL-supplied teacher name plus any character aliases — Mikey
      // dances as Madame Olga, for example.
      const name = this.teacherParam.toLowerCase()
      if (!name) return []
      const aliases = TEACHER_ALIASES[name] || []
      return [name, ...aliases]
    },
    myClasses () {
      const names = this.matchNames
      if (!names.length) return []
      return this.classes.filter(c => {
        const t = (c.teacher || '').toLowerCase()
        if (!t) return false
        const parts = t.split(',').map(s => s.trim())
        return names.some(n => parts.includes(n))
      })
    },
    daysWithClasses () {
      const seen = new Set()
      for (const c of this.myClasses) {
        if (DAY_LABEL[c.day]) seen.add(c.day)
      }
      return Object.keys(DAY_LABEL)
        .filter(d => seen.has(d))
        .map(d => ({ key: d, label: DAY_LABEL[d], dayOrder: DAY_ORDER[d] }))
        .sort((a, b) => a.dayOrder - b.dayOrder)
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    const cols = iabfForYear(this.festivalYear)
    const snap = await db.collection(cols.classes).get()
    this.classes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    this.loading = false
    if (this.daysWithClasses.length > 0) {
      this.activeDay = this.daysWithClasses[0].key
    }
  },
  methods: {
    classesForDay (day) {
      return this.myClasses
        .filter(c => c.day === day)
        .slice()
        .sort((a, b) => {
          const ka = classSortKey(a)
          const kb = classSortKey(b)
          if (ka.start !== kb.start) return ka.start - kb.start
          return kb.end - ka.end
        })
    },
    mealsForDay (day) {
      // All-track classes are meals + day-wide announcements. Surface
      // them as small markers interleaved with the teacher's classes so
      // they know when to be at the dining hall.
      return this.classes
        .filter(c => c.day === day && c.track === 'all')
        .slice()
        .sort((a, b) => {
          const ka = classSortKey(a)
          const kb = classSortKey(b)
          return ka.start - kb.start
        })
    },
    dayTimeline (day) {
      const items = [
        ...this.classesForDay(day).map(c => ({ ...c, kind: 'class' })),
        ...this.mealsForDay(day).map(m => ({ ...m, kind: 'meal' }))
      ]
      return items.sort((a, b) => {
        const ka = classSortKey(a)
        const kb = classSortKey(b)
        if (ka.start !== kb.start) return ka.start - kb.start
        return kb.end - ka.end
      })
    },
    trackLabelsFor (c) {
      const ts = Array.isArray(c.tracks) && c.tracks.length ? c.tracks : (c.track ? [c.track] : [])
      return ts.map(t => TRACK_LABEL[t] || t).join(' · ')
    }
  }
}
</script>

<style scoped>
.teacher-itin { max-width: 760px; padding-bottom: 60px; }
h1 { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 400; font-size: 28px; margin: 12px 0 4px; color: var(--iabf-ink, #1c1b1f); }
.muted { color: var(--iabf-ink-faint, #8a8792); font-style: italic; font-size: 14px; }
.pad-y { padding: 30px 0; }
.empty-state { padding: 30px 0; font-family: var(--iabf-font-body, inherit); }
.empty-state code { background: #f5efe0; padding: 1px 6px; border-radius: 3px; font-family: monospace; font-size: 13px; }

.day-tabs { margin: 14px 0 6px; }
.class-list { display: flex; flex-direction: column; gap: 14px; padding: 12px 0; }
.class-card { background: #fff; border: 1px solid var(--iabf-border, #e3e0e8); border-radius: 6px; padding: 14px 16px; box-shadow: 0 1px 0 rgba(0,0,0,.04); }
.meal-row { display: flex; align-items: center; gap: 10px; padding: 6px 14px; background: #e7f1f6; border: 1px solid #b6d4e2; border-radius: 6px; font-family: var(--iabf-font-body, inherit); font-size: 13px; color: #1a4452; }
.meal-time { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 700; min-width: 100px; white-space: nowrap; }
.meal-title { flex: 1; font-weight: 600; }
.meal-room { color: var(--iabf-primary, #8a957d); }
.card-meta { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; font-family: var(--iabf-font-body, inherit); font-size: 14px; color: #555; margin-bottom: 8px; }
.meta-time { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 700; color: var(--iabf-ink, #1c1b1f); font-size: 18px; }
.meta-room { color: var(--iabf-primary, #8a957d); font-weight: 700; }
.meta-tracks { background: #f5efe0; padding: 2px 8px; border-radius: 10px; font-size: 12px; color: #7a6828; }
.meta-piano { background: #fdecc9; color: #8a5a10; padding: 2px 8px; border-radius: 10px; font-size: 12px; font-weight: 700; }

.card-title { font-family: var(--iabf-font-display, Georgia, serif); font-weight: 700; font-size: 20px; color: var(--iabf-ink, #1c1b1f); line-height: 1.25; }
.card-note { font-family: var(--iabf-font-body, inherit); font-style: italic; color: #7a6828; margin-top: 4px; font-size: 14px; }
.card-desc { font-family: var(--iabf-font-body, inherit); font-size: 14px; color: var(--iabf-ink-muted, #5c5a63); line-height: 1.5; margin: 10px 0 0; white-space: pre-wrap; }
</style>
